import * as React from "react";
import { cn } from "@repo/utils/cn";
import { ComponentProps } from "react";
import Image from "next/image";
import { parseAiAlt } from "@repo/ai-disclaimer";
import { AiDisclaimerForAlt } from "./AiDisclaimer";
import { Link } from "./Link";
import { LinkProps } from "./types";
import { getImageSizes } from "./utils";

export interface CardLocationsProps
  extends Omit<ComponentProps<"div">, "children"> {
  image?: {
    src: string;
    alt?: string;
    width?: number;
    height?: number;
  } | null;
  tariff?: string;
  title?: string;
  text?: string;
  link?: LinkProps | null;
  aspectRatio?: "square" | "tall";
  sizes?: string;
  onClick?: () => void;
}

export const CardLocations: React.FC<CardLocationsProps> = ({
  image,
  tariff,
  title,
  text,
  link,
  aspectRatio = "tall",
  sizes,
  onClick,
  className,
  ...props
}) => {
  const imageSizes = sizes || getImageSizes(image?.src);
  const hasTariff =
    typeof tariff === "string" ? tariff.trim().length > 0 : Boolean(tariff);

  // Add "from " prefix if tariff starts with currency symbol without "from"
  const formattedTariff = React.useMemo(() => {
    if (!tariff) return tariff;
    const trimmed = tariff.trim().toLowerCase();
    if (trimmed.startsWith("from")) return tariff;
    return `from ${tariff}`;
  }, [tariff]);

  const cardClasses = cn(
    "uipm:group uipm:relative uipm:overflow-hidden uipm:rounded-[var(--radius-lg)]",
    "uipm:flex uipm:flex-col uipm:justify-end",
    "uipm:min-h-[300px]",
    "uipm:h-full",
    "uipm:cursor-pointer",
    aspectRatio === "square"
      ? "uipm:aspect-[1/1]"
      : "uipm:aspect-[291/416] uipm:min-w-[291px]",
    link && "uipm:no-underline",
    className,
  );

  const cardContent = (
    <>
      {image && (
        <>
          <div className="uipm:absolute uipm:inset-0 uipm:transition-transform uipm:duration-300 uipm:ease-out uipm:group-hover:scale-110">
            <Image
              src={image.src}
              alt={parseAiAlt(image.alt).cleanAlt}
              fill
              sizes={imageSizes}
              className="uipm:object-cover uipm:rounded-[var(--radius-sm)]"
            />
          </div>
          {/* Outside the scaling wrapper: the label must not zoom with the image. */}
          <AiDisclaimerForAlt alt={image.alt} src={image.src} />
        </>
      )}

      {/* Gradient Overlay */}
      <div
        className={cn(
          "uipm:absolute uipm:inset-0",
          "uipm:bg-[linear-gradient(180deg,rgba(34,68,47,0)0%,rgba(34,68,47,1)59.62%)]",
          "uipm:z-1",
          "uipm:opacity-80 uipm:transition-opacity uipm:duration-300 uipm:ease-out uipm:group-hover:opacity-40",
        )}
      />

      {/* Content Section */}
      <div
        className={cn(
          "uipm:flex uipm:flex-col",
          "uipm:pt-[var(--container-section-bottom)]",
          "uipm:px-[var(--padding-padding-sm-plus)]",
          "uipm:pb-[var(--container-card-overlay-padding)]",
          "uipm:z-2",
        )}
      >
        {hasTariff && (
          <div
            className={cn(
              "uipm:text-[var(--typography-colors-link)]",
              "uipm:text-(length:--typography-sizes-body-small)",
              "uipm:font-normal",
              "uipm:leading-[var(--typography-line-heights-body-xs)]",
              "uipm:bg-[var(--surface-surface-primary)]",
              "uipm:rounded-[var(--radius-radius-full)]",
              "uipm:px-[var(--margin-margin-sm-plus)]",
              "uipm:py-[6px]",
              "uipm:inline-block uipm:w-fit",
              "uipm:mb-4",
            )}
          >
            {formattedTariff}
          </div>
        )}

        {title && (
          <h3
            className={cn(
              "uipm:font-[var(--font-family-sans)]",
              "uipm:font-medium",
              "uipm:text-[var(--typography-colors-text-inverted)]",
              "uipm:text-(length:--typography-sizes-body-large)",
              "uipm:leading-[var(--typography-line-heights-body-large)]",
              "uipm:tracking-[0px]",
              text && "uipm:mb-2",
            )}
          >
            {title}
          </h3>
        )}

        {text && (
          <p
            className={cn(
              "uipm:font-[var(--font-family-sans)]",
              "uipm:text-[var(--typography-colors-text-inverted)]",
              "uipm:text-(length:--typography-sizes-body-small)",
              "uipm:font-normal",
              "uipm:leading-[var(--typography-line-heights-body-xs)]",
            )}
          >
            {text}
          </p>
        )}
      </div>
    </>
  );

  if (link) {
    return (
      <Link
        href={link.href}
        target={link.target}
        noFollow={link.noFollow}
        onClick={onClick}
        className={cardClasses}
        {...(props as ComponentProps<"a">)}
      >
        {cardContent}
      </Link>
    );
  }

  return (
    <div className={cardClasses} onClick={onClick} {...props}>
      {cardContent}
    </div>
  );
};
